import { Injectable, inject } from '@angular/core';
import { openDB, DBSchema, IDBPDatabase } from 'idb';
import { NetworkService } from './network.service';
import type {
  Visita,
  Productor,
  TipoVisita,
  ObjetivoVisita,
  ModalidadContacto,
  CategoriaAnexoResponse,
} from './visitas.service';
import type { ProductorPlanNegocioItem } from './negocio.service';

const DB_NAME = 'indap_ruta_dia_db';
const DB_VERSION = 4;

export interface RutaDiaSyncMeta {
  key: 'default';
  lastSyncAt: string;
  idPerfil: number;
}

export interface RutaDiaItemLista {
  id: string;
  idProductor: string;
  nombreProductor: string;
  subtituloTipo: string;
  hora: string;
  lugar: string;
  estado: string;
  fechaIso: string;
}

/** `crear_visita` (agendar offline) o `registrar_visita` (POST /visita/{id}/registrar). */
export type PendienteSubidaKind = 'crear_visita' | 'registrar_visita';

export interface PendienteSubidaItem {
  id: string;
  idProductor: string;
  nombreProductor: string;
  subtituloTipo: string;
  fechaHora: string;
  lugar: string;
  estado: string;
  creadoEn: string;
  /** Por defecto `crear_visita` si no viene (compatibilidad). */
  kind?: PendienteSubidaKind;
  /** Para `registrar_visita`: UUID de la visita en servidor (no confundir con `id` del pendiente). */
  idVisita?: string;
  /** Para POST /visita/crear al subir desde el dashboard (registros nuevos lo incluyen). */
  idTipoVisita?: number;
  bolsaVisita?: boolean;
  esContingencia?: boolean;
  descripcionEmergencia?: string | null;
  /** Descripción de la visita (POST /visita/crear). */
  descripcion?: string;
  comentarios?: string;
  /** Campos solo para `registrar_visita` (reconstrucción de FormData al subir). */
  desarrolloAsesoria?: string;
  fechaVisitaRegistro?: string;
  horaInicioRegistro?: string;
  horaTerminoRegistro?: string | null;
  tipoReporte?: 'digital' | 'papel';
  /** @deprecated usar idObjetivosVisita */
  idObjetivoVisita?: number;
  idObjetivosVisita?: number[];
  idModalidadContacto?: number;
  folio?: string;
  generarFolio?: boolean;
  recomendaciones?: string;
  compromisos?: string;
  /** Número de filas de archivo guardadas en `pendiente_registro_blobs`. */
  archivosCount?: number;
}

export interface PendienteRegistroBlobRow {
  key: string;
  pendienteId: string;
  index: number;
  nombre: string;
  mimeType: string;
  idCategoria: number;
  data: ArrayBuffer;
}

type RutaDiaDB = DBSchema & {
  sync_meta: { key: string; value: RutaDiaSyncMeta };
  productores: { key: string; value: Productor & { id_productor: string } };
  planes: { key: string; value: ProductorPlanNegocioItem };
  visitas: { key: string; value: Visita };
  ruta_lista: { key: string; value: RutaDiaItemLista };
  pendientes_subida: { key: string; value: PendienteSubidaItem };
  tipos_visita: { key: number; value: TipoVisita };
  objetivos_visita: { key: number; value: ObjetivoVisita };
  modalidades_contacto: { key: number; value: ModalidadContacto };
  categorias_anexo: { key: number; value: CategoriaAnexoResponse };
  pendiente_registro_blobs: { key: string; value: PendienteRegistroBlobRow };
};

function blobKey(pendienteId: string, index: number): string {
  return `${pendienteId}#${index}`;
}

@Injectable({
  providedIn: 'root',
})
export class RutaDiaStorageService {
  private readonly networkService = inject(NetworkService);
  private db: IDBPDatabase<RutaDiaDB> | null = null;
  /** Evita dos `openDB` concurrentes (misma versión), que en WebView Android puede colgarse. */
  private dbAbriendo: Promise<IDBPDatabase<RutaDiaDB>> | null = null;
  /**
   * Último estado de red efectivo visto en `dbReady`. Si el evento offline falla o llega tarde,
   * igual detectamos el cambio aquí y no reutilizamos una conexión IDB abierta “en online”.
   */
  private ultimoEstadoOnlineEnDb: boolean | null = null;
  /** Evita `close()` de IndexedDB en medio de una escritura (WebView Android / evento `offline`). */
  private idbWriteDepth = 0;
  private cierreDbDiferidoPorEscritura = false;

  constructor() {
    if (typeof window === 'undefined') {
      return;
    }
    /**
     * En WebView/Capacitor, al pasar de online → offline sin matar el proceso la conexión IDB
     * puede quedar en estado inválido: lecturas puntuales (p. ej. `planes`) se cuelgan mientras
     * otras stores siguen respondiendo. Cerrar y volver a abrir en el próximo `dbReady()` lo corrige.
     */
    let estadoAnterior = this.networkService.isOnline();
    this.networkService.getNetworkStatus().subscribe(online => {
      if (estadoAnterior && !online) {
        this.soltarConexionDbTrasPerdidaDeRed();
      }
      estadoAnterior = online;
    });
  }

  private cerrarConexionDbAhora(): void {
    const instancia = this.db;
    this.db = null;
    try {
      instancia?.close();
    } catch {
      /* conexión ya cerrada o estado inválido */
    }
  }

  private soltarConexionDbTrasPerdidaDeRed(): void {
    if (this.idbWriteDepth > 0) {
      this.cierreDbDiferidoPorEscritura = true;
      return;
    }
    this.cerrarConexionDbAhora();
  }

  private async withIdbWrite<T>(fn: () => Promise<T>): Promise<T> {
    this.idbWriteDepth++;
    try {
      return await fn();
    } finally {
      this.idbWriteDepth--;
      if (this.idbWriteDepth === 0 && this.cierreDbDiferidoPorEscritura) {
        this.cierreDbDiferidoPorEscritura = false;
        this.cerrarConexionDbAhora();
      }
    }
  }

  /** Si cambió la conectividad respecto al último acceso IDB, la conexión en caché puede estar rota (WebView). */
  private invalidarDbSiCambioNavigatorOnline(): void {
    const online = this.networkService.isOnline();
    if (this.ultimoEstadoOnlineEnDb === null) {
      this.ultimoEstadoOnlineEnDb = online;
      return;
    }
    if (this.ultimoEstadoOnlineEnDb !== online) {
      if (this.db) {
        this.soltarConexionDbTrasPerdidaDeRed();
      }
      this.ultimoEstadoOnlineEnDb = online;
    }
  }

  private horaDesdeIso(fechaIso: string): string {
    try {
      const d = new Date(fechaIso);
      if (Number.isNaN(d.getTime())) return '';
      return d.toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' });
    } catch {
      return '';
    }
  }

  private fechaSoloDesdeIso(fechaIso: string): string {
    try {
      const d = new Date(fechaIso);
      if (Number.isNaN(d.getTime())) return '';
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    } catch {
      return '';
    }
  }

  private async dbReady(): Promise<IDBPDatabase<RutaDiaDB>> {
    this.invalidarDbSiCambioNavigatorOnline();
    if (this.db) {
      return this.db;
    }
    if (!this.dbAbriendo) {
      this.dbAbriendo = openDB<RutaDiaDB>(DB_NAME, DB_VERSION, {
        upgrade(database, oldVersion) {
          if (!database.objectStoreNames.contains('sync_meta')) {
            database.createObjectStore('sync_meta', { keyPath: 'key' });
          }
          if (!database.objectStoreNames.contains('productores')) {
            database.createObjectStore('productores', { keyPath: 'id_productor' });
          }
          if (!database.objectStoreNames.contains('planes')) {
            database.createObjectStore('planes', { keyPath: 'id_productor' });
          }
          if (!database.objectStoreNames.contains('visitas')) {
            database.createObjectStore('visitas', { keyPath: 'id_visita' });
          }
          if (!database.objectStoreNames.contains('ruta_lista')) {
            database.createObjectStore('ruta_lista', { keyPath: 'id' });
          }
          if (!database.objectStoreNames.contains('pendientes_subida')) {
            database.createObjectStore('pendientes_subida', { keyPath: 'id' });
          }
          if (!database.objectStoreNames.contains('tipos_visita')) {
            database.createObjectStore('tipos_visita', { keyPath: 'id_tipo_visita' });
          }
          if (oldVersion < 3) {
            if (!database.objectStoreNames.contains('objetivos_visita')) {
              database.createObjectStore('objetivos_visita', { keyPath: 'id_objetivo_visita' });
            }
            if (!database.objectStoreNames.contains('categorias_anexo')) {
              database.createObjectStore('categorias_anexo', { keyPath: 'id_categoria_anexo' });
            }
            if (!database.objectStoreNames.contains('pendiente_registro_blobs')) {
              database.createObjectStore('pendiente_registro_blobs', { keyPath: 'key' });
            }
          }
          if (oldVersion < 4) {
            if (!database.objectStoreNames.contains('modalidades_contacto')) {
              database.createObjectStore('modalidades_contacto', { keyPath: 'id_modalidad_contacto' });
            }
          }
        },
      })
        .then(database => {
          database.onversionchange = () => {
            try {
              database.close();
            } catch {
              /* noop */
            }
            if (this.db === database) {
              this.db = null;
            }
          };
          this.db = database;
          return database;
        })
        .finally(() => {
          this.dbAbriendo = null;
        });
    }
    return this.dbAbriendo;
  }

  async guardarPostSync(payload: {
    meta: RutaDiaSyncMeta;
    productores: Productor[];
    planes: ProductorPlanNegocioItem[];
    visitas: Visita[];
    lista: RutaDiaItemLista[];
    tiposVisita: TipoVisita[];
    objetivosVisita: ObjetivoVisita[];
    modalidadesContacto: ModalidadContacto[];
    categoriasAnexo: CategoriaAnexoResponse[];
  }): Promise<void> {
    await this.withIdbWrite(async () => {
    const database = await this.dbReady();
    // Conservar pendientes locales: `ruta_lista` se reemplaza con datos del servidor.
    const pendientesPrevios = await database.getAll('pendientes_subida');

    const tx = database.transaction(
      [
        'sync_meta',
        'productores',
        'planes',
        'visitas',
        'ruta_lista',
        'tipos_visita',
        'objetivos_visita',
        'modalidades_contacto',
        'categorias_anexo',
      ],
      'readwrite'
    );
    await tx.objectStore('sync_meta').put(payload.meta);
    const prodStore = tx.objectStore('productores');
    await prodStore.clear();
    for (const p of payload.productores) {
      const idProd = String(p.id_productor ?? '').trim();
      if (!idProd) continue;
      await prodStore.put({ ...(p as Productor & { id_productor: string }), id_productor: idProd });
    }
    const planStore = tx.objectStore('planes');
    await planStore.clear();
    for (const pl of payload.planes) {
      const idProd = String(pl.id_productor ?? '').trim();
      if (!idProd) continue;
      await planStore.put({ ...pl, id_productor: idProd });
    }
    const visStore = tx.objectStore('visitas');
    await visStore.clear();
    for (const v of payload.visitas) {
      await visStore.put(v);
    }
    const listaStore = tx.objectStore('ruta_lista');
    await listaStore.clear();
    for (const row of payload.lista) {
      await listaStore.put(row);
    }

    const tiposStore = tx.objectStore('tipos_visita');
    await tiposStore.clear();
    for (const t of payload.tiposVisita) {
      await tiposStore.put(t);
    }

    const objStore = tx.objectStore('objetivos_visita');
    await objStore.clear();
    for (const o of payload.objetivosVisita) {
      await objStore.put(o);
    }
    const modStore = tx.objectStore('modalidades_contacto');
    await modStore.clear();
    for (const m of payload.modalidadesContacto) {
      await modStore.put(m);
    }
    const catStore = tx.objectStore('categorias_anexo');
    await catStore.clear();
    for (const c of payload.categoriasAnexo) {
      await catStore.put(c);
    }

    // Reinyectar agendamientos offline y marcar registros pendientes en la hoja de ruta.
    for (const p of pendientesPrevios) {
      const kind = p.kind ?? 'crear_visita';
      if (kind === 'crear_visita') {
        await listaStore.put({
          id: p.id,
          idProductor: String(p.idProductor ?? '').trim(),
          nombreProductor: p.nombreProductor || '',
          subtituloTipo: p.subtituloTipo || '',
          hora: this.horaDesdeIso(p.fechaHora),
          lugar: p.lugar || '',
          estado: 'Agendada',
          fechaIso: this.fechaSoloDesdeIso(p.fechaHora) || p.fechaHora,
        });
      } else if (kind === 'registrar_visita') {
        const idVisita = String(p.idVisita ?? '').trim();
        if (!idVisita) continue;
        const fila = await listaStore.get(idVisita);
        if (fila) {
          await listaStore.put({
            ...fila,
            estado: 'Finalizada (pendiente de subir)',
          });
        }
        const vis = await visStore.get(idVisita);
        if (vis) {
          await visStore.put({
            ...vis,
            estado: 'Finalizada (pendiente de subir)',
          });
        }
      }
    }

    await tx.done;
    });
  }

  async obtenerMeta(): Promise<RutaDiaSyncMeta | undefined> {
    const database = await this.dbReady();
    return database.get('sync_meta', 'default');
  }

  async obtenerListaRuta(): Promise<RutaDiaItemLista[]> {
    const database = await this.dbReady();
    return database.getAll('ruta_lista');
  }

  async obtenerPendientesSubida(): Promise<PendienteSubidaItem[]> {
    const database = await this.dbReady();
    return database.getAll('pendientes_subida');
  }

  /**
   * Pendiente `registrar_visita` más reciente para una visita (mismo UUID que `ruta_lista.id` / `id_visita`).
   * Sirve para rehidratar el formulario offline al reabrir «Registrar Visita».
   */
  async obtenerPendienteRegistrarVisitaMasReciente(idVisita: string): Promise<PendienteSubidaItem | undefined> {
    const id = String(idVisita ?? '').trim();
    if (!id) return undefined;
    const todos = await this.obtenerPendientesSubida();
    const candidatos = todos.filter(p => {
      const mismoIdVisita = String(p.idVisita ?? '').trim() === id;
      if (!mismoIdVisita) return false;
      if (p.kind === 'registrar_visita') return true;
      if (p.kind == null && p.idVisita != null) return true;
      return false;
    });
    if (!candidatos.length) return undefined;
    candidatos.sort((a, b) => {
      const ta = new Date(a.creadoEn || 0).getTime();
      const tb = new Date(b.creadoEn || 0).getTime();
      return tb - ta;
    });
    return candidatos[0];
  }

  async obtenerTiposVisitaLocal(): Promise<TipoVisita[]> {
    const database = await this.dbReady();
    const out = await database.getAll('tipos_visita');
    return out.sort((a, b) => (a.descripcion || '').localeCompare(b.descripcion || '', 'es'));
  }

  async obtenerObjetivosVisitaLocal(): Promise<ObjetivoVisita[]> {
    const database = await this.dbReady();
    const out = await database.getAll('objetivos_visita');
    return out.sort((a, b) => (a.descripcion || '').localeCompare(b.descripcion || '', 'es'));
  }

  async obtenerModalidadesContactoLocal(): Promise<ModalidadContacto[]> {
    const database = await this.dbReady();
    const out = await database.getAll('modalidades_contacto');
    return out.sort((a, b) => (a.descripcion || '').localeCompare(b.descripcion || '', 'es'));
  }

  async obtenerCategoriasAnexoLocal(): Promise<{ id: number; descripcion: string }[]> {
    const database = await this.dbReady();
    const raw = await database.getAll('categorias_anexo');
    return raw
      .map(c => ({ id: c.id_categoria_anexo, descripcion: c.descripcion || '' }))
      .sort((a, b) => a.descripcion.localeCompare(b.descripcion, 'es'));
  }

  async obtenerProductoresLocal(): Promise<Productor[]> {
    const database = await this.dbReady();
    return database.getAll('productores');
  }

  async obtenerProductorLocal(idProductor: string): Promise<Productor | undefined> {
    const database = await this.dbReady();
    return database.get('productores', idProductor);
  }

  /**
   * Lectura O(1) por clave. No usar `getAll('planes')`: tras sincronizar todo el listado negocio
   * el store puede tener miles de filas y bloquear el hilo principal (UI pegada en «Cargando»).
   */
  async obtenerPlanLocal(idProductor: string): Promise<ProductorPlanNegocioItem | undefined> {
    const database = await this.dbReady();
    const raw = String(idProductor ?? '');
    const id = raw.trim();
    if (!id) return undefined;
    const variantes = [id, id.toLowerCase(), id.toUpperCase()].filter(
      (k, i, arr) => k.length > 0 && arr.indexOf(k) === i
    );
    for (const key of variantes) {
      const fila = await database.get('planes', key);
      if (fila) return fila;
    }
    return undefined;
  }

  async obtenerVisitaLocal(idVisita: string): Promise<Visita | undefined> {
    const database = await this.dbReady();
    return database.get('visitas', idVisita);
  }

  async buscarProductoresLocal(termino: string, max: number = 50): Promise<Productor[]> {
    const q = (termino || '').trim().toLowerCase();
    const all = await this.obtenerProductoresLocal();
    if (!q) return all.slice(0, max);
    const match = (p: Productor) => {
      const parts = [p.nombre, p.primer_apellido, p.segundo_apellido, p.rut, p.jerarquia?.descripcion]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return parts.includes(q);
    };
    const res: Productor[] = [];
    for (const p of all) {
      if (match(p)) {
        res.push(p);
        if (res.length >= max) break;
      }
    }
    return res;
  }

  async agregarPendientePlaceholder(ejemplo: PendienteSubidaItem): Promise<void> {
    const database = await this.dbReady();
    await database.put('pendientes_subida', ejemplo);
  }

  /**
   * Guarda una visita creada offline como pendiente de subir y la agrega a la hoja de ruta local.
   * Se usa al agendar desde `/ruta-del-dia` sin conexión.
   */
  async agregarPendienteSubidaDesdeAgendar(payload: {
    id: string;
    idProductor: string;
    nombreProductor: string;
    subtituloTipo: string;
    fechaHoraIso: string;
    lugar: string;
    idTipoVisita: number;
    idObjetivosVisita?: number[];
    bolsaVisita?: boolean;
    esContingencia: boolean;
    descripcionEmergencia?: string | null;
    descripcion: string;
    comentarios?: string;
  }): Promise<void> {
    const creadoEn = new Date().toISOString();
    const idProductor = String(payload.idProductor ?? '').trim();

    const pendiente: PendienteSubidaItem = {
      id: payload.id,
      kind: 'crear_visita',
      idProductor,
      nombreProductor: payload.nombreProductor,
      subtituloTipo: payload.subtituloTipo,
      fechaHora: payload.fechaHoraIso,
      lugar: payload.lugar,
      estado: 'Pendiente de subir',
      creadoEn,
      idTipoVisita: payload.idTipoVisita,
      idObjetivosVisita: payload.idObjetivosVisita?.length ? payload.idObjetivosVisita : undefined,
      bolsaVisita: payload.bolsaVisita === true,
      esContingencia: payload.esContingencia,
      descripcionEmergencia: payload.descripcionEmergencia ?? null,
      descripcion: payload.descripcion,
      comentarios: payload.comentarios ?? '',
    };

    const fila: RutaDiaItemLista = {
      id: payload.id,
      idProductor,
      nombreProductor: payload.nombreProductor,
      subtituloTipo: payload.subtituloTipo,
      hora: this.horaDesdeIso(payload.fechaHoraIso),
      lugar: payload.lugar,
      estado: 'Agendada',
      fechaIso: this.fechaSoloDesdeIso(payload.fechaHoraIso) || payload.fechaHoraIso,
    };

    await this.withIdbWrite(async () => {
      const db = await this.dbReady();
      const tx = db.transaction(['pendientes_subida', 'ruta_lista'], 'readwrite');
      await tx.objectStore('pendientes_subida').put(pendiente);
      await tx.objectStore('ruta_lista').put(fila);
      await tx.done;
    });
  }

  /**
   * Registro offline de realización de visita: pendiente con id propio; no borra la fila de ruta por UUID.
   */
  async agregarPendienteRegistrarVisita(payload: {
    pendienteId: string;
    idVisita: string;
    idProductor: string;
    nombreProductor: string;
    subtituloTipo: string;
    lugarVisita: string;
    fechaHoraVisitaAgendadaIso: string;
    desarrolloAsesoria: string;
    fechaVisitaRegistro: string;
    horaInicio: string;
    horaTermino: string | null;
    tipoReporte: 'digital' | 'papel';
    idObjetivosVisita: number[];
    idModalidadContacto: number;
    bolsaVisita?: boolean;
    folio: string;
    generarFolio?: boolean;
    recomendaciones: string;
    compromisos: string;
    archivos: { file: File; idCategoria: number }[];
    /** Si se reutiliza un pendiente existente, conservar `creadoEn` original (orden en dashboard). */
    creadoEnExistente?: string;
  }): Promise<void> {
    const creadoEn = (payload.creadoEnExistente ?? '').trim() || new Date().toISOString();

    const archivosPreparados = await Promise.all(
      payload.archivos.map(async a => ({
        data: await a.file.arrayBuffer(),
        nombre: a.file.name,
        mimeType: a.file.type || 'application/octet-stream',
        idCategoria: a.idCategoria,
      }))
    );

    await this.withIdbWrite(async () => {
      const database = await this.dbReady();

      const pendiente: PendienteSubidaItem = {
        id: payload.pendienteId,
        kind: 'registrar_visita',
        idVisita: payload.idVisita,
        idProductor: payload.idProductor,
        nombreProductor: payload.nombreProductor,
        subtituloTipo: payload.subtituloTipo,
        fechaHora: payload.fechaHoraVisitaAgendadaIso,
        lugar: payload.lugarVisita,
        estado: 'Registrar visita (pendiente)',
        creadoEn,
        desarrolloAsesoria: payload.desarrolloAsesoria,
        fechaVisitaRegistro: payload.fechaVisitaRegistro,
        horaInicioRegistro: payload.horaInicio,
        horaTerminoRegistro: payload.horaTermino,
        tipoReporte: payload.tipoReporte,
        idObjetivosVisita: payload.idObjetivosVisita,
        idModalidadContacto: payload.idModalidadContacto,
        bolsaVisita: payload.bolsaVisita === true,
        folio: payload.folio,
        generarFolio: payload.generarFolio === true,
        recomendaciones: payload.recomendaciones,
        compromisos: payload.compromisos,
        archivosCount: payload.archivos.length,
      };

      const tx = database.transaction(
        ['pendientes_subida', 'pendiente_registro_blobs', 'visitas', 'ruta_lista'],
        'readwrite'
      );
      const blobStore = tx.objectStore('pendiente_registro_blobs');
      await this.borrarBlobsDePendienteEnStore(blobStore, payload.pendienteId);
      await tx.objectStore('pendientes_subida').put(pendiente);

      let idx = 0;
      for (const a of archivosPreparados) {
        const row: PendienteRegistroBlobRow = {
          key: blobKey(payload.pendienteId, idx),
          pendienteId: payload.pendienteId,
          index: idx,
          nombre: a.nombre,
          mimeType: a.mimeType,
          idCategoria: a.idCategoria,
          data: a.data,
        };
        await tx.objectStore('pendiente_registro_blobs').put(row);
        idx++;
      }

      const vis = await tx.objectStore('visitas').get(payload.idVisita);
      if (vis) {
        const actualizada: Visita = {
          ...vis,
          estado: 'Finalizada (pendiente de subir)',
          fecha_visita: payload.fechaVisitaRegistro,
          hora_inicio: payload.horaInicio,
          hora_termino: payload.horaTermino ?? undefined,
          desarrollo_asesoria: payload.desarrolloAsesoria,
          recomendaciones: payload.recomendaciones,
          compromisos: payload.compromisos,
        };
        await tx.objectStore('visitas').put(actualizada);
      }

      const fila = await tx.objectStore('ruta_lista').get(payload.idVisita);
      if (fila) {
        await tx.objectStore('ruta_lista').put({
          ...fila,
          estado: 'Finalizada (pendiente de subir)',
        });
      }

      await tx.done;
    });
  }

  async obtenerBlobsRegistroPendiente(pendienteId: string): Promise<PendienteRegistroBlobRow[]> {
    const database = await this.dbReady();
    const store = database.transaction('pendiente_registro_blobs').store;
    const out: PendienteRegistroBlobRow[] = [];
    let cursor = await store.openCursor();
    while (cursor) {
      const v = cursor.value as PendienteRegistroBlobRow;
      if (v.pendienteId === pendienteId) {
        out.push(v);
      }
      cursor = await cursor.continue();
    }
    out.sort((a, b) => a.index - b.index);
    return out;
  }

  /** Borra filas de `pendiente_registro_blobs` cuyo `pendienteId` coincide (cursor en la tx actual). */
  private async borrarBlobsDePendienteEnStore(blobStore: any, pendienteId: string): Promise<void> {
    const keys: string[] = [];
    let cursor = await blobStore.openCursor();
    while (cursor) {
      const v = cursor.value as PendienteRegistroBlobRow;
      if (v.pendienteId === pendienteId) {
        keys.push(v.key);
      }
      cursor = await cursor.continue();
    }
    for (const k of keys) {
      await blobStore.delete(k);
    }
  }

  /**
   * Quita un pendiente de subida y su fila asociada en la hoja de ruta local (mismo `id`).
   * Solo para `crear_visita` / agendamientos offline (`offline-…`).
   */
  async eliminarPendienteSubida(id: string): Promise<void> {
    await this.withIdbWrite(async () => {
      const database = await this.dbReady();
      const tx = database.transaction(['pendientes_subida', 'ruta_lista', 'pendiente_registro_blobs'], 'readwrite');
      const blobStore = tx.objectStore('pendiente_registro_blobs');
      await this.borrarBlobsDePendienteEnStore(blobStore, id);
      await tx.objectStore('pendientes_subida').delete(id);
      await tx.objectStore('ruta_lista').delete(id);
      await tx.done;
    });
  }

  /**
   * Elimina solo el pendiente y sus blobs (p. ej. tras subir `registrar_visita`); no toca `ruta_lista`.
   */
  async eliminarPendienteSubidaSinRutaLista(id: string): Promise<void> {
    await this.withIdbWrite(async () => {
      const database = await this.dbReady();
      const tx = database.transaction(['pendientes_subida', 'pendiente_registro_blobs'], 'readwrite');
      await this.borrarBlobsDePendienteEnStore(tx.objectStore('pendiente_registro_blobs'), id);
      await tx.objectStore('pendientes_subida').delete(id);
      await tx.done;
    });
  }
}
